import {
  Factory,
  Model,
  Response,
  belongsTo,
  hasMany,
  trait,
  type ModelInstance,
} from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import type { PaymentMethodData } from '@/composables/payments'
import type { InvoiceData, InvoiceRowData } from '@/composables/api/Invoice'
import type { MirageServer, MockServer } from '@/api/mock-server/server.js'
import type { SchemaAttributes } from '../types.js'
import {
  PaginationError,
  createErrorResponse,
  pageable,
  filterable,
  generateFinanceAmount,
} from '@/api/mock-server/utils.js'
import { ClientStatusOptions } from '@/models/client.js'
import {
  InvoicePaymentStatus,
  InvoiceStatus,
  InvoiceType,
} from '@/models/invoice.js'
import { PaymentMethod } from '@/models/payments.js'
import { ConsultationStatuses } from '@/models/consultation/index.js'
import {
  rand,
  randSoonDate,
  randRecentDate,
  randNumber,
  randBetweenDate,
  randFullName,
  randBoolean,
  randChanceBoolean,
  randParagraph,
  randCompanyName,
  randEmail,
  randStreetAddress,
  randCity,
  randState,
  randZipCode,
  randMask,
} from '@ngneat/falso'
import { addToDate } from '@/utils/date/calculation.js'
import { formatToDateTimeISO, formatToJSDate } from '@/utils/date/conversion.js'
import { getTodayJSDate } from '@/utils/date/getter.js'
import { roundEven, safeMathRandom, toNumber } from '@/utils/number.js'
import { getEntityFullName } from '@/utils/string.js'
import { buildLegacyProvetUrl } from '@/utils/url.js'

/**
 * Replace symbols in a pattern string (simple implementation for ### pattern)
 */
const replaceSymbols = (pattern: string): string => {
  return pattern.replace(/#/g, () => randNumber({ min: 0, max: 9 }).toString())
}

/**
 * Get a payment date for an invoice.
 * 60% chance: Payment on same day as invoice (immediate payment)
 * 40% chance: Payment 1-6 months after invoice (delayed payment)
 */
const getPaymentDate = (invoiceDate: string) => {
  const useImmediatePayment = randChanceBoolean({ chanceTrue: 0.6 })
  if (useImmediatePayment) {
    return invoiceDate
  }

  return addToDate(invoiceDate, {
    months: randNumber({ min: 1, max: 6 }),
    days: randNumber({ min: 0, max: 30 }),
  })!
}

export const model = Model.extend({
  rows: hasMany('invoiceRow'),
  payments: hasMany('invoicePayment'),
  department: belongsTo('department'),
  consultation: belongsTo('consultation'),
  client: belongsTo('client'),
  invoicingClient: belongsTo('client'),
  createdUser: belongsTo('user'),
  modifiedUser: belongsTo('user'),
  patients: hasMany('patient'),
  relatedConsolidatedInvoice: belongsTo('invoice'),
  finalization: belongsTo('invoiceFinalization'),
  clientStatement: belongsTo('clientStatement'),
  tags: hasMany('tag', { inverse: null }),
  creditNotes: hasMany('invoice', { inverse: null }),
  finalizedCreditNotes: hasMany('invoice', { inverse: 'originalInvoice' }),
  originalInvoice: belongsTo('invoice', { inverse: 'finalizedCreditNotes' }),
  lastInvoiceEvent: belongsTo('invoiceEventLog'),
  supervisingVeterinarian: belongsTo('user'),
}) as ModelDefinition<InvoiceData>

function getInvoiceTotals(
  invoiceRows: ModelInstance<Partial<InvoiceRowData>>[],
) {
  const totalWithVat = invoiceRows.reduce(
    (sum: number, row: ModelInstance<Partial<InvoiceRowData>>) =>
      sum + toNumber(row.attrs.sum_with_vat),
    0,
  )
  const total = invoiceRows.reduce(
    (sum: number, row: ModelInstance<Partial<InvoiceRowData>>) =>
      sum + toNumber(row.attrs.sum_without_vat),
    0,
  )
  const totalVat = roundEven(totalWithVat - total, 4)

  return {
    total,
    totalVat,
    totalWithVat,
  }
}

/**
 * Evaluates whether a credit note can be created for an invoice.
 * Mirrors the backend logic from can_credit_note.
 */
function evaluateCanCreditNote(
  invoice: ModelInstance<
    InvoiceData & {
      rows?: ModelInstance<InvoiceRowData>[]
      finalization?: ModelInstance<InvoiceData>
      relatedConsolidatedInvoice?: ModelInstance<InvoiceData>
    }
  >,
): boolean {
  // Must have rows (unless consolidated)
  const hasRows = invoice.rows && invoice.rows?.length
  if (!hasRows && !invoice.consolidated_invoice) {
    return false
  }

  // Must be finalized
  if (!invoice.finalization) {
    return false
  }

  // Cannot be consolidated or part of consolidated
  if (invoice.consolidated_invoice || invoice.relatedConsolidatedInvoice) {
    return false
  }

  // Cannot be a credit note itself
  if (invoice.credit_note) {
    return false
  }

  return true
}

export const factory = Factory.extend<InvoiceData>({
  created() {
    return randRecentDate().toISOString()
  },
  invoice_date() {
    const createdDate = this.created?.toString()
    return randBetweenDate({
      from: formatToJSDate(createdDate ? createdDate : getTodayJSDate()),
      to: randSoonDate(),
    }).toISOString()
  },
  invoice_due_date() {
    const invoiceDate = this.invoice_date?.toString()
    const dueDate = addToDate(invoiceDate ? invoiceDate : getTodayJSDate(), {
      days: 30,
    })
    return dueDate.toISOString()
  },
  modified() {
    return this.invoice_date ? this.invoice_date.toString() : undefined
  },
  internal_remarks() {
    return randParagraph()
  },
  remarks() {
    return randParagraph()
  },
  date_paid() {
    return this.invoice_date?.toString()
  },
  countersale() {
    return randBoolean()
  },
  credit_note() {
    return !this.countersale
  },
  can_credit_note() {
    return false
  },
  status() {
    return rand([
      InvoiceStatus.Draft,
      InvoiceStatus.Sent,
      InvoiceStatus.Open,
      InvoiceStatus.Finalized,
      InvoiceStatus.Invoicing,
      InvoiceStatus.Voided,
    ])
  },
  invoice_type() {
    return rand([InvoiceType.Consultation, InvoiceType.CounterSale])
  },
  invoice_number() {
    return replaceSymbols('21240000###')
  },
  total_with_vat() {
    return roundEven(toNumber(generateFinanceAmount({ min: 200, max: 500 })), 4)
  },
  total_vat() {
    return roundEven(toNumber(this.total_with_vat) * 0.23, 4)
  },
  total() {
    return roundEven(
      toNumber(this.total_with_vat) - toNumber(this.total_vat),
      4,
    )
  },
  total_paid() {
    return roundEven(
      toNumber(
        rand([
          this.total,
          generateFinanceAmount({ min: 0, max: toNumber(this.total) }),
        ]),
      ),
    )
  },
  due_sum() {
    return roundEven(
      toNumber(this.total_with_vat) - toNumber(this.total_paid),
      4,
    )
  },
  client_due_sum() {
    return roundEven(
      toNumber(this.total_with_vat) - toNumber(this.total_paid),
      4,
    )
  },
  waiting_for_insurance_sum() {
    return 0
  },
  unpaid_coupons_sum() {
    return 0
  },
  vat_determining_type() {
    return rand(['VAT_DETERMINING_INCL_VAT', 'VAT_DETERMINING_EXCL_VAT'])
  },
  vat_calculation_method() {
    return rand([
      'VAT_CALCULATION_BY_ITEM',
      'VAT_CALCULATION_BY_INVOICE_ROW',
      'VAT_CALCULATION_BY_TOTAL',
    ])
  },
  provet_invoice_page() {
    // Will be generated in `afterCreate`.
    return ''
  },
  consolidated_invoice() {
    return false
  },
  due_sum_at_end_date() {
    return this.due_sum
  },

  // Payer data
  payer_name() {
    return randFullName()
  },
  payer_email() {
    return randEmail({
      firstName: this.payer_name?.toString().split(' ')[0],
      lastName: this.payer_name?.toString().split(' ')[1],
      provider: 'example.org',
    })
  },
  payer_organization_name() {
    return randCompanyName()
  },
  payer_vat_number() {
    return randMask('###############').toString()
  },
  payer_register_number() {
    return randMask('#########').toString()
  },
  payer_street_address() {
    return randStreetAddress()
  },
  payer_street_address_2() {
    if (randChanceBoolean({ chanceTrue: 0.3 }) && this.country === 'US') {
      return randStreetAddress()
    }
    return undefined
  },
  payer_street_address_3() {
    if (randChanceBoolean({ chanceTrue: 0.1 }) && this.country === 'US') {
      return randStreetAddress()
    }
    return undefined
  },
  payer_zip_code() {
    return randZipCode()
  },
  payer_city() {
    return randCity()
  },
  payer_state() {
    return rand(['CA', 'NY', 'TX', 'FL', undefined])
  },
  payer_country() {
    return rand(['GB', 'NL', 'FI', 'US'])
  },
  payer_country_region() {
    return rand([randState(), undefined])
  },
  payer_einvoice_address() {
    return randStreetAddress()
  },
  payer_einvoice_operator() {
    return randCompanyName()
  },
  payer_id_number() {
    return randMask('##########').toString()
  },
  payer_status() {
    return rand(Object.values(ClientStatusOptions))
  },
  has_credit_notes() {
    return false
  },

  afterCreate(invoice, server) {
    invoice.provet_invoice_page = buildLegacyProvetUrl({
      provetId: '922',
      path: `/billing/invoice_finished/${invoice.id}/`,
    }).toString()

    // Assign a client to the invoice if there is none
    if (!invoice.client) {
      const client = server.create(
        'client',
        'withSecondaryOwner',
        'withPatients',
      )
      invoice.update({
        client,
        invoicingClient: client,
        payer_name: getEntityFullName(client),
        payer_email: client.email,
        payer_organization_name: client.organization_name,
        payer_vat_number: client.vat_number,
        payer_register_number: client.register_number,
        payer_street_address: client.street_address,
        payer_street_address_2: client.street_address_2,
        payer_street_address_3: client.street_address_3,
        payer_zip_code: client.zip_code,
        payer_city: client.city,
        payer_state: client.state,
        payer_country: client.country,
        payer_country_region: client.country_region,
        payer_einvoice_address: undefined,
        payer_einvoice_operator: undefined,
        payer_id_number: client.id_number,
        payer_status: client.status,
      })
    }

    // Add a created and modifier user
    if (!invoice.createdUser) {
      const user = server.schema.first('user')
      invoice.update({
        createdUser: user,
        modifiedUser: user,
      })
    }

    // Assign a patient from the client's patients to the invoice
    const clientId = invoice?.clientId as string
    const patients = clientId
      ? (server as MockServer).schema
          .all('patient')
          .models.filter((patient) => patient?.clientId === clientId)
      : []
    if (patients?.length) {
      const patient = patients[Math.floor(safeMathRandom() * patients.length)]
      invoice.update({ patients: [patient] })
    }

    // Assign a consultation to the invoice
    if (invoice.invoice_type === InvoiceType.Consultation) {
      const consultation = server.create('consultation', {
        invoice,
        client: invoice.client,
        department: invoice.department,
        patients: invoice.patients.models,
      })
      invoice.update({
        consultation,
        countersale: false,
        credit_note: false,
      })
    }

    if (invoice.invoice_type === InvoiceType.CounterSale) {
      invoice.update({
        countersale: true,
        credit_note: false,
        consultation: null,
      })
    }

    if (invoice.invoice_type === InvoiceType.CreditNote) {
      invoice.update({
        credit_note: true,
        countersale: false,
        consultation: null,
        can_credit_note: false, // Credit notes cannot be credited
      })

      const parentInvoice = invoice.originalInvoice
      if (parentInvoice) {
        parentInvoice.update({
          has_credit_notes: true,
        })
      }
    }

    // Evaluate can_credit_note for all invoices after creation
    if (!invoice.credit_note) {
      invoice.update({
        can_credit_note: evaluateCanCreditNote(invoice),
      })
    }
  },

  withRows: trait({
    afterCreate(invoice, server) {
      // Update Rows
      if (!invoice.rows?.length) {
        const invoiceRows = server.createList('invoiceRow', 5, {
          invoice,
        })

        // Add a Patient that belongs to the Invoice's Client
        // to most of the InvoiceRows.
        const clientId = invoice?.clientId as string
        const patients = clientId
          ? (server as MockServer).schema
              .all('patient')
              .models.filter((patient) => patient?.clientId === clientId)
          : []

        if (patients?.length) {
          const patientIds = patients.map((patient) => patient.attrs.id)
          const invoicePatientsSet = new Set<string>()

          invoiceRows.forEach((invoiceRow) => {
            const shouldAddPatient = randChanceBoolean({ chanceTrue: 0.9 })
            if (!shouldAddPatient) {
              return
            }

            const patientId = rand(patientIds)
            invoiceRow.update({
              patientId,
            })
            invoicePatientsSet.add(patientId)
          })

          // Associate patients with the invoice
          const invoicePatients = Array.from(invoicePatientsSet)
            .map((patientId) => patients.find((p) => p.attrs.id === patientId))
            .filter(Boolean)

          if (invoicePatients.length > 0) {
            invoice.update({ patients: invoicePatients })
          }
        }

        // Update Invoice Totals
        const invoiceTotals = getInvoiceTotals(invoiceRows)
        invoice.update({
          rows: invoiceRows,
          total_with_vat: invoiceTotals.totalWithVat,
          total_vat: invoiceTotals.totalVat,
          total: invoiceTotals.total,
        })

        // Re-evaluate can_credit_note after adding rows
        invoice.update({
          can_credit_note: evaluateCanCreditNote(invoice),
        })

        // Randomly select one InvoiceRow to associate with the StockLocation
        const randomIndex = randNumber({
          min: 0,
          max: invoiceRows.length - 1,
        })
        const randomInvoiceRow = invoiceRows[randomIndex]

        // Update the selected InvoiceRow with the StockLocations
        const firstDepartment = server.schema.first('department')
        if (firstDepartment) {
          const stockLocation = server.create('stockLocation', {
            id: '1',
            department: firstDepartment,
            is_default: false,
          })
          randomInvoiceRow.update({ stockLocations: [stockLocation] })
        }
      }

      // Update Client
      if (!invoice.client) {
        const client = server.create('client', 'withSecondaryOwner')
        invoice.update({ client })
      }
    },
  }),

  withFinalization: trait({
    afterCreate(invoice, server) {
      invoice.update({
        status: InvoiceStatus.Finalized,
        finalization: server.create('invoiceFinalization'),
      })

      // Re-evaluate can_credit_note after finalizing
      invoice.update({
        can_credit_note: evaluateCanCreditNote(invoice),
      })
    },
  }),

  withNoPayments: trait({
    afterCreate(invoice) {
      invoice.total_paid = 0
      invoice.due_sum = invoice.total_with_vat
      invoice.client_due_sum = invoice.total_with_vat
      invoice.due_sum_at_end_date = invoice.total_with_vat
    },
  }),

  withPartialPayment: trait({
    afterCreate(invoice, server) {
      const partialPaymentMethodCode = rand(Object.values(PaymentMethod))
      const paidAmount = 30

      const paymentDate = getPaymentDate(invoice.invoice_date)
      const invoicePayment = server.create('invoicePayment', {
        paid: paidAmount,
        date_added: formatToDateTimeISO(paymentDate)!,
        paymentMethod: server.schema.findBy('paymentMethod', {
          code: partialPaymentMethodCode,
        } as ModelInstance<PaymentMethodData>),
        status: InvoicePaymentStatus.Paid,
      })

      const dueSum = roundEven(toNumber(invoice.total_with_vat) - paidAmount, 4)

      invoice.update({
        total_paid: paidAmount,
        due_sum: dueSum,
        client_due_sum: dueSum,
        due_sum_at_end_date: dueSum,
        paymentIds: [invoicePayment.id],
      })
    },
  }),

  withFullPayment: trait({
    afterCreate(invoice, server) {
      const fullPaymentMethodCode = rand(Object.values(PaymentMethod))

      const paymentDate = getPaymentDate(invoice.invoice_date)
      const invoicePayment = server.create('invoicePayment', {
        paid: toNumber(invoice.total_with_vat),
        date_added: formatToDateTimeISO(paymentDate)!,
        paymentMethod: server.schema.findBy('paymentMethod', {
          code: fullPaymentMethodCode,
        } as ModelInstance<PaymentMethodData>),
        status: InvoicePaymentStatus.Paid,
      })

      invoice.update({
        paymentIds: [invoicePayment.id],
        total_paid: invoice.total_with_vat,
        due_sum: 0,
        client_due_sum: 0,
        due_sum_at_end_date: 0,
      })
    },
  }),

  withConsolidatedInvoice: trait({
    afterCreate(invoice, server) {
      const relatedConsolidatedInvoice = server.create(
        'invoice',
        'withFinalization',
        'withRows',
        'withFullPayment',
        {
          department: server.schema.first('department'),
          credit_note: false,
          consolidated_invoice: true,
          can_credit_note: false, // Consolidated invoices cannot be credited
        },
      )

      invoice.update({
        relatedConsolidatedInvoice,
        can_credit_note: false, // Invoices part of consolidated cannot be credited
      })
    },
  }),

  withNoPrimaryClient: trait({
    afterCreate(invoice) {
      invoice.client.update({
        first_name: undefined,
        last_name: undefined,
      })
    },
  }),

  withNoSecondaryClient: trait({
    afterCreate(invoice) {
      const client = invoice.client
      client.update({
        secondary_owner_first_name: undefined,
        secondary_owner_last_name: undefined,
      })
      invoice.update({ client })
    },
  }),

  withNoCompany: trait({
    afterCreate(invoice) {
      const client = invoice.client
      client.update({
        customer_type: 'CUSTOMER_TYPE_PRIVATE',
        organization_name: null,
      })
      invoice.update({ client })
    },
  }),

  withFinalizedConsultation: trait({
    afterCreate(invoice, server) {
      // Use only the first patient for single-patient consultation
      const patient = invoice.patients.models[0]
      const complaintType = server.create('complaintType', {
        name: 'Vaccination',
      })
      const consultation = server.create(
        'consultation',
        'withDischargeInstructions',
        'withDiagnoses',
        {
          invoice,
          client: invoice.client,
          department: invoice.department,
          patients: patient ? [patient] : [],
          status: ConsultationStatuses.Finalized,
          complaintType,
          complaint: 'Annual vaccination and health check',
        },
      )
      invoice.update({
        consultation,
        countersale: false,
        credit_note: false,
        invoice_type: InvoiceType.Consultation,
      })
    },
  }),

  withFinalizedMultiPatientConsultation: trait({
    afterCreate(invoice, server) {
      const complaintType = server.create('complaintType', {
        name: 'Gastrointestinal',
      })
      const consultation = server.create(
        'consultation',
        'withDischargeInstructions',
        'withDiagnoses',
        {
          invoice,
          client: invoice.client,
          department: invoice.department,
          patients: invoice.patients?.models ?? [],
          status: ConsultationStatuses.Finalized,
          complaintType,
          complaint: 'Vomiting and loss of appetite for 2 days',
        },
      )
      invoice.update({
        consultation,
        countersale: false,
        credit_note: false,
        invoice_type: InvoiceType.Consultation,
      })
    },
  }),

  withCreditNote: trait({
    afterCreate(invoice, server) {
      // Note: In the backend, there's additional logic to check if rows are already
      // fully credited (partial crediting). This mock simplifies by creating a full
      // credit note without checking for existing partial credits.

      // Copy rows from the original invoice with negative amounts
      const originalRows = invoice.rows.models
      const creditNoteRows: ModelInstance<Partial<InvoiceRowData>>[] = []

      originalRows.forEach(
        (originalRow: ModelInstance<Partial<InvoiceRowData>>) => {
          const creditNoteRow = server.create('invoiceRow', {
            ...originalRow.attrs,
            primary_display_price:
              toNumber(originalRow.attrs.primary_display_price) * -1,
            sum_without_vat: toNumber(originalRow.attrs.sum_without_vat) * -1,
            sum_with_vat: toNumber(originalRow.attrs.sum_with_vat) * -1,
            sum_vat: toNumber(originalRow.attrs.sum_vat) * -1,
            // @ts-expect-error - patient is a relationship on the model
            patient: originalRow.patient,
          })
          creditNoteRows.push(creditNoteRow)
        },
      )

      // Calculate negative totals for the credit note
      const creditNoteTotals = getInvoiceTotals(creditNoteRows)

      // Create the credit note invoice (copy attributes but not relationships)
      const creditNote = server.create('invoice', {
        // Copy basic invoice attributes
        client: invoice.client,
        invoicingClient: invoice.invoicingClient,
        department: invoice.department,
        patients: invoice.patients.models,

        // Copy payer information
        payer_name: invoice.payer_name,
        payer_email: invoice.payer_email,
        payer_organization_name: invoice.payer_organization_name,
        payer_vat_number: invoice.payer_vat_number,
        payer_register_number: invoice.payer_register_number,
        payer_street_address: invoice.payer_street_address,
        payer_street_address_2: invoice.payer_street_address_2,
        payer_street_address_3: invoice.payer_street_address_3,
        payer_zip_code: invoice.payer_zip_code,
        payer_city: invoice.payer_city,
        payer_state: invoice.payer_state,
        payer_country: invoice.payer_country,
        payer_country_region: invoice.payer_country_region,
        payer_einvoice_address: invoice.payer_einvoice_address,
        payer_einvoice_operator: invoice.payer_einvoice_operator,
        payer_id_number: invoice.payer_id_number,
        payer_status: invoice.payer_status,

        // Copy VAT settings
        vat_determining_type: invoice.vat_determining_type,
        vat_calculation_method: invoice.vat_calculation_method,

        // Use same dates
        invoice_date: invoice.invoice_date,
        created: invoice.created,
        modified: invoice.invoice_date,

        // Set the relationship to the original invoice
        originalInvoice: invoice,
        rows: creditNoteRows,

        // Set negative totals to represent refund
        total: creditNoteTotals.total,
        total_vat: creditNoteTotals.totalVat,
        total_with_vat: creditNoteTotals.totalWithVat,

        // Credit notes start with no payments
        total_paid: 0,
        due_sum: creditNoteTotals.totalWithVat,
        client_due_sum: creditNoteTotals.totalWithVat,
        due_sum_at_end_date: creditNoteTotals.totalWithVat,
        waiting_for_insurance_sum: 0,
        unpaid_coupons_sum: 0,

        // Finalize the credit note
        finalization: server.create('invoiceFinalization'),

        // Set invoice type and flags
        invoice_type: InvoiceType.CreditNote,
        credit_note: true,
        countersale: false,
        consultation: null,

        // Set created and modified users
        createdUser: invoice.createdUser || server.schema.first('user'),
        modifiedUser: invoice.modifiedUser || server.schema.first('user'),
      }) as ModelInstance<Partial<InvoiceData>>

      // Update the credit note's provet_invoice_page and invoice_number
      creditNote.update({
        invoice_number: `CN-${creditNote.attrs.invoice_number}`,
        provet_invoice_page: buildLegacyProvetUrl({
          provetId: '922',
          path: `/billing/invoice_finished/${creditNote.id}/`,
        }).toString(),
      })

      // Update the credit note's can_credit_note (credit notes cannot be credited)
      creditNote.update({
        can_credit_note: false,
      })

      // Update the original invoice to link to the credit note
      const invoiceType =
        invoice.invoice_type === InvoiceType.CreditNote
          ? rand([InvoiceType.Consultation, InvoiceType.CounterSale])
          : invoice.invoice_type
      invoice.update({
        credit_note: false,
        has_credit_notes: true,
        invoice_type: invoiceType,
        countersale: invoiceType === InvoiceType.CounterSale,
        ...(!invoice.finalization
          ? {
              status: InvoiceStatus.Finalized,
              finalization: server.create('invoiceFinalization'),
            }
          : {}),
      })

      // Re-evaluate can_credit_note for the original invoice
      // (in the backend, it checks if fully credited, but we simplify here)
      invoice.update({
        can_credit_note: evaluateCanCreditNote(invoice),
      })
    },
  }),

  withTags: trait({
    afterCreate(invoice, server) {
      if (!invoice.tags?.length) {
        const tagTexts = server.createList(
          'tagText',
          randNumber({ min: 2, max: 3 }),
        )

        const tags = tagTexts.map((tagText) =>
          server.create('tag', { tagText }),
        )
        invoice.update({ tags })
      }
    },
  }),
})

export function routes(server: MockServer) {
  server.resource('invoice', { path: '/invoices' })

  server.get('/invoices', function (this: MirageServer, schema, request) {
    const invoices = schema.all('invoice')

    try {
      // Apply filtering first (before pagination)
      const filtered = filterable(invoices, request, {
        client_id: (invoice, value) => {
          // Handle both string and number comparison
          return String(invoice.clientId) === String(value)
        },
        'finalization.isnull': (invoice, value) => {
          const hasFinalization = !!invoice.finalization
          return value === 'true' ? !hasFinalization : hasFinalization
        },
        'due_sum.gt': (invoice, value) => {
          const due = toNumber(invoice.due_sum)
          const threshold = toNumber(value)
          return due > threshold
        },
      })

      // Then apply pagination
      const { data, meta } = pageable(filtered, request)

      return {
        ...this.serialize(data),
        meta: {
          pagination: meta,
        },
      }
    } catch (e) {
      if (e instanceof PaginationError) {
        return new Response(404, {}, e.message)
      }

      // Fallback for unexpected errors
      return new Response(500, {}, 'Internal Server Error')
    }
  })

  server.get('/invoices/:id', function (schema, request) {
    const id = request.params.id
    const invoice = schema.find('invoice', id)

    if (!invoice) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `Invoice with id ${id} not found` }],
      })
    }

    return invoice
  })

  server.patch('/invoices/:id/internal_remarks', function (schema, request) {
    const id = request.params.id
    const invoice = schema.find('invoice', id)
    const requestBody = JSON.parse(request.requestBody)

    if (!invoice) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `Invoice with id ${id} not found` }],
      })
    }

    invoice.update({
      internal_remarks: requestBody.data.attributes.internal_remarks,
    })

    return invoice
  })

  server.patch('/invoices/:id/public_remarks', function (schema, request) {
    const id = request.params.id
    const invoice = schema.find('invoice', id)
    const requestBody = JSON.parse(request.requestBody)

    if (!invoice) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `Invoice with id ${id} not found` }],
      })
    }

    invoice.update({ remarks: requestBody.data.attributes.remarks })

    return invoice
  })

  server.patch(
    '/invoices/:id/supervising_veterinarian',
    function (schema, request) {
      const id = request.params.id
      const invoice = schema.find('invoice', id)
      const requestBody = JSON.parse(request.requestBody)

      if (!invoice) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: `Invoice with id ${id} not found` }],
        })
      }

      const vetData =
        requestBody.data?.relationships?.supervising_veterinarian?.data
      const vet = vetData ? schema.find('user', vetData.id) : null

      invoice.update({ supervisingVeterinarian: vet })

      // Also update the consultation's supervising veterinarian,
      // since the page reads the vet from the consultation relationship
      if (invoice.consultation) {
        invoice.consultation.update({ supervisingVeterinarian: vet })
      }

      if (requestBody.data?.attributes?.update_invoice_rows && invoice.rows) {
        invoice.rows.models.forEach(
          (row: ModelInstance<Partial<InvoiceRowData>>) => {
            // @ts-expect-error - supervisingVeterinarian is a relationship on the model
            row.update({ supervisingVeterinarian: vet })
          },
        )
      }

      return invoice
    },
  )

  server.get('/invoices/:id/pdf', function (schema, request) {
    const id = request.params.id
    const invoice = schema.find('invoice', id)

    if (!invoice) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `Invoice with id ${id} not found` }],
      })
    }

    // Get the pdf_type from query params (defaults to 'normal')
    const pdfType = request.queryParams?.pdf_type || 'normal'

    return {
      data: {
        id: invoice.id,
        type: 'Base64Document',
        attributes: {
          content: btoa(`Invoice PDF - Type: ${pdfType}`),
          filename: `invoice-${invoice.id}-${pdfType}.pdf`,
          content_type: 'application/pdf',
        } satisfies SchemaAttributes<'Base64Document'>,
      },
    }
  })

  server.get('/invoices/:id/consultation-summary', function (schema, request) {
    const id = request.params.id
    const invoice = schema.find('invoice', id)

    if (!invoice) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `Invoice with id ${id} not found` }],
      })
    }

    const consultation = invoice.consultation
    if (!consultation) {
      return { data: [] }
    }

    // Get patients from the consultation
    const patients = consultation.patients?.models || []
    const patientSummaries: SchemaAttributes<'PatientSummary'>[] = patients.map(
      (patient: { id: string; name: string }) => ({
        id: Number(patient.id),
        name: patient.name || 'Unknown Patient',
        species: 'Dog',
        breed: 'Mixed Breed',
        critical_notes: null,
      }),
    )

    // Get diagnoses from the consultation
    const diagnosisModels = consultation.consultationDiagnoses?.models || []
    const diagnoses: SchemaAttributes<'DiagnosisSummary'>[] =
      diagnosisModels.map(
        (diagnosis: {
          id: string
          name: string
          code: string
          patient: { id: string } | null
        }) => ({
          id: Number(diagnosis.id),
          name: diagnosis.name || '',
          code: diagnosis.code || '',
          patient_id: diagnosis.patient ? Number(diagnosis.patient.id) : null,
        }),
      )

    // Get discharge instructions from the consultation
    const dischargeInstructionModels =
      consultation.consultationDischargeInstructions?.models || []
    const dischargeInstructions: SchemaAttributes<'DischargeInstructionSummary'>[] =
      dischargeInstructionModels.map(
        (instruction: {
          id: string
          text: string
          patient: { id: string } | null
        }) => ({
          id: Number(instruction.id),
          text: instruction.text || '',
          patient_id: instruction.patient
            ? Number(instruction.patient.id)
            : null,
          all_patients: !instruction.patient,
          hide_client: false,
        }),
      )

    return {
      data: [
        {
          id: consultation.id,
          type: 'ConsultationSummary',
          attributes: {
            status: consultation.status || 'FINALIZED',
            consultation_type: consultation.consultation_type || 'OUTPATIENT',
            complaint:
              consultation.complaint || 'Vomiting and loss of appetite',
            complaint_type: consultation.complaintType
              ? {
                  id: Number(consultation.complaintType.id),
                  name: consultation.complaintType.name || '',
                }
              : null,
            started: consultation.started || randRecentDate().toISOString(),
            ended: consultation.ended || randRecentDate().toISOString(),
            supervising_veterinarian: {
              id: 1,
              first_name: 'Dr. Sarah',
              last_name: 'Johnson',
            },
            ward: {
              id: 1,
              name: 'General Ward',
              code: 'GW-01',
            },
            patients: patientSummaries,
            consultation_diagnosis: diagnoses,
            consultation_dischargeinstructions: dischargeInstructions,
          },
        },
      ],
    }
  })
}

export const seedInvoiceData = (server: MockServer) => {
  // Get or create specific client for testing
  let testClient = server.schema.find('client', '22')
  if (!testClient) {
    testClient = server.create('client', { id: '22' })
  }

  // Ensure department exists
  const department =
    server.schema.first('department') ||
    server.create('department', { name: 'Default Department' })

  // Create invoices for client ID "22" to match the test URL
  // #1. Draft Invoice - with no Invoice rows
  server.create('invoice', 'withNoPayments', {
    department,
    client: testClient,
    credit_note: false,
    status: InvoiceStatus.Draft,
  })

  // #2. Draft Invoice - with Invoice rows
  server.create('invoice', 'withRows', 'withNoPayments', {
    department,
    client: testClient,
    credit_note: false,
    status: InvoiceStatus.Draft,
  })

  // #3. Finalized unpaid Invoice
  server.create('invoice', 'withFinalization', 'withRows', 'withNoPayments', {
    department,
    client: testClient,
    credit_note: false,
  })

  // #4. Finalized partially paid Invoice
  server.create(
    'invoice',
    'withFinalization',
    'withRows',
    'withPartialPayment',
    {
      department,
      client: testClient,
      credit_note: false,
    },
  )

  // #5. Finalized fully paid Invoice
  server.create('invoice', 'withFinalization', 'withRows', 'withFullPayment', {
    department,
    client: testClient,
    credit_note: false,
  })

  // #6. Finalized fully paid Invoice - with Consolidated Invoice
  // #7. Related Consolidated Invoice
  server.create(
    'invoice',
    'withFinalization',
    'withRows',
    'withFullPayment',
    'withConsolidatedInvoice',
    {
      department,
      client: testClient,
      credit_note: false,
    },
  )

  // #8. With a company client - only company avatar displayed
  server.create('invoice', 'withFinalization', 'withRows', {
    department,
    client: testClient,
    credit_note: false,
  })

  // #9. With a primary client and a secondary client
  server.create('invoice', 'withFinalization', 'withRows', 'withNoCompany', {
    department,
    client: testClient,
    credit_note: false,
  })

  // #10. Only a primary client
  server.create(
    'invoice',
    'withFinalization',
    'withRows',
    'withNoSecondaryClient',
    'withNoCompany',
    {
      department,
      client: testClient,
      credit_note: false,
    },
  )

  // #11. Finalized fully paid Invoice - with Consultation
  server.create(
    'invoice',
    'withFinalization',
    'withRows',
    'withFullPayment',
    'withFinalizedConsultation',
    'withTags',
    {
      department: server.schema.first('department'),
      credit_note: false,
    },
  )

  // #12. Finalized Invoice with Credit Note
  // #13. Invoice of the Credit Note
  server.create(
    'invoice',
    'withFinalization',
    'withRows',
    'withFullPayment',
    'withCreditNote',
    {
      department,
      client: testClient,
      credit_note: false,
    },
  )

  // Create some general invoices for other clients as well
  server.create('invoice', 'withFinalization', 'withRows', 'withFullPayment', {
    department,
    credit_note: false,
  })

  server.create(
    'invoice',
    'withFinalization',
    'withRows',
    'withPartialPayment',
    {
      department,
      credit_note: false,
    },
  )

  // #16. Finalized Invoice - with Consultation (multiple patients)
  server.create(
    'invoice',
    'withFinalization',
    'withRows',
    'withNoPayments',
    'withFinalizedMultiPatientConsultation',
    {
      department: server.schema.first('department'),
      credit_note: false,
    },
  )
}

export function seeds(server: MockServer) {
  seedInvoiceData(server)
}
